import React, { type ReactElement, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import {
  selectDomain,
  selectIsTenant,
  useDomainStore,
} from '@/store/use-domain.store';
import classNames from 'classnames';

import CartWidget from '@/common/components/cart-widget';
import { AppbarSearch } from '@/common/components/homeSearch/appbar-search.component';
import UserProfile from '@/common/components/user-profile/user-profile.components';
import Wrapper from '@/common/components/wrapper/wrapper.component';
import { useSession } from '@/common/hooks/use-session.hook';
import { useShowAppbarSearch } from '@/common/hooks/use-show-appbar-search.hook';

import Logo from '~/public/assets/images/svg/logo-just-text.svg';
import SearchIcon from '~/public/assets/images/svg/search.svg';

import { type AppbarProps } from './appbar.component';
import LoginPopUp from './loginPopUp/loginPopUp.component';

const getAboutUsConfig = (isTenant: boolean, config: any) => {
  const isTenantAbousUsPageActive = config?.pagesStatus.contentAboutUs;
  const hasTenantAboutusPage = isTenant && isTenantAbousUsPageActive;
  return !isTenant || hasTenantAboutusPage;
};

function DesktopAppbarSearch({
  isTenant,
  shopTitle,
}: {
  isTenant: boolean;
  shopTitle?: string;
}) {
  if (isTenant) {
    return (
      <Link
        href="/search"
        className="flex h-10 w-full max-w-[360px] items-center justify-between gap-2 rounded-xl border border-silver-200 bg-silver-100 px-4 text-sm font-normal text-black-200"
      >
        <span className="min-w-0 truncate">
          {`جست و جو در ${shopTitle ?? ''}`}
        </span>
        <SearchIcon className="h-5 w-5 shrink-0" />
      </Link>
    );
  }

  return <AppbarSearch />;
}

function DesktopAppbarBrand({
  isTenant,
  config,
  Heading,
}: {
  isTenant: boolean;
  config: ReturnType<typeof selectDomain>;
  Heading: 'h1' | 'h2';
}) {
  return (
    <Link
      href="/"
      className="relative flex gap-x-4 min-h-[44px] min-w-[44px] items-center justify-center rounded-md "
      tabIndex={0}
    >
      {isTenant && config?.logoUrl ? (
        <div className="relative h-[50px] w-[50px]">
          <Image
            src={config.logoUrl}
            alt={config.title || 'لوگوی فروشگاه'}
            fill
            className="object-contain"
            sizes="50px"
          />
        </div>
      ) : null}
      {!isTenant && <Logo className="h-[20px]" />}
      {isTenant && (
        <Heading className="text-xl font-semibold lg:min-w-fit whitespace-nowrap">
          {config?.title}
        </Heading>
      )}
    </Link>
  );
}

export default function DesktopAppbar({
  cartWidget = true,
  title,
  className,
  layoutChildrenClassNames,
  showSearch = false,
}: AppbarProps): ReactElement {
  const [openLogin, setOpenLogin] = useState(false);
  const [getStatus, setGetStatus] = useState<boolean | undefined>();
  const { status } = useSession();

  const isTenant = useDomainStore(selectIsTenant);
  const config = useDomainStore(selectDomain);
  const showAboutUslink = getAboutUsConfig(isTenant, config);
  const shouldShowSearch = useShowAppbarSearch(showSearch);

  const pathname = usePathname();
  const Heading = pathname === '/' ? 'h1' : 'h2';

  const cancelLogin = () => {
    setOpenLogin(false);
    setGetStatus(!getStatus);
  };

  return (
    <div
      className={classNames(
        'hidden h-[76px] w-full bg-white lg:block shadow-lg',
        className
      )}
    >
      <Wrapper className="px-3">
        <div
          className={classNames(
            'mx-auto flex h-[76px] max-w-[1166px] flex-row items-center justify-between'
          )}
        >
          <div className="flex flex-row items-center justify-start gap-x-8 w-fit">
            <DesktopAppbarBrand
              isTenant={isTenant}
              config={config}
              Heading={Heading}
            />
            {showAboutUslink ? (
              <Link
                className="hidden whitespace-nowrap text-sm font-semibold lg:block"
                target="blank"
                href={isTenant ? '/about-us' : 'https://witro.shop/mag'}
              >
                {isTenant ? 'درباره ما' : 'درباره ویترو'}
              </Link>
            ) : null}
          </div>

          {title && !shouldShowSearch ? (
            <h3 className="text-nowrap text-center text-[14px] font-medium text-black-default">
              {title}
            </h3>
          ) : null}

          {shouldShowSearch ? (
            <DesktopAppbarSearch
              isTenant={isTenant}
              shopTitle={config?.title}
            />
          ) : null}

          <div
            className={`flex items-center justify-end ${
              status === 'authenticated' ? '!h-[44px]' : '!h-[36px]'
            }`}
          >
            {cartWidget ? (
              <CartWidget
                id="store-click-cart-v1"
                iconClassName={layoutChildrenClassNames?.cartClassName}
              />
            ) : null}
            <UserProfile
              onLoginClick={() => {
                setOpenLogin(true);
              }}
            />
          </div>
        </div>
      </Wrapper>

      <LoginPopUp isOpen={openLogin} onCancel={cancelLogin} />
    </div>
  );
}
